import { pull } from 'lodash-es'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { KOL_SELF_LAYOUT_HEADER_HEIGHT } from '@/components/layout/header'
import { TrialCounterHeight } from '@/components/layout/header/trial-counter'
import { IRM_HEADER_HEIGHT } from '@/components/layout/irm/irm-header'
import {
  PUBLIC_HEADER_HEIGHT,
  PUBLIC_MOBILE_HEADER_HEIGHT,
} from '@/components/public-page/header/app-header-pc'
import {
  AGENCY_PAGES,
  BILLBOARD_MULTI_DIMENSION_PAGES,
  BILLBOARD_PAGES,
  KOL_PAGES,
  Page,
} from '@/hooks/use-authorization/constants'
import getPage from '@/hooks/use-authorization/get-page'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'

const competitivePages = [
  Page.CompetitiveBrandPlatformsAnalyses,
  Page.CompetitiveBrandComprehensiveAnalyses,
  Page.CompetitiveKeywordContentAnalyses,
  Page.CompetitiveKeywordStatisticsAnalyses,
  Page.CompetitiveMentionStatisticsAnalyses,
  Page.CompetitiveMentionContentAnalyses,
]

const predictiveReportDetailPages = [
  Page.PredictiveReportIdList,
  Page.PredictiveReportIdReport,
]

const useHeaderHeight = (): number => {
  const pathname = usePathname()
  const breakpoint = useBreakpoint()

  const { data: userStatus } = useFetchUserStatusQuery()

  const page = pathname ? getPage(pathname) : undefined
  const isPublicKolDetailPage = useIsPublicDetailPage()
  const isBillboardPage = page
    ? [...BILLBOARD_PAGES, ...BILLBOARD_MULTI_DIMENSION_PAGES].includes(page)
    : false

  const workspacePages: string[] = useMemo(
    () => [
      Page.Settings,
      Page.SettingsPassword,
      Page.WorkspaceSetting,
      Page.WorkspaceMember,
      Page.WorkspacePlan,
      Page.WorkspaceHistory,
      Page.WorkspaceValuation,
    ],
    [],
  )
  const kolDetailPages = [
    Page.PublicKolDetail,
    Page.KolId,
    Page.KolSelf,
    Page.KolAgencyId,
  ]

  const noHeaderHeightPages = [
    ...kolDetailPages,
    ...competitivePages,
    ...predictiveReportDetailPages,
  ]

  const kolRelatedPage: Page[] = pull(
    KOL_PAGES.concat(AGENCY_PAGES),
    ...kolDetailPages,
  )

  if (isBillboardPage) {
    return breakpoint === 'mobile'
      ? PUBLIC_MOBILE_HEADER_HEIGHT
      : PUBLIC_HEADER_HEIGHT
  }

  if (isPublicKolDetailPage) {
    return PUBLIC_HEADER_HEIGHT
  }

  if (page && kolRelatedPage.includes(page)) {
    return KOL_SELF_LAYOUT_HEADER_HEIGHT
  }

  if (page && noHeaderHeightPages.includes(page)) {
    return 0
  }

  if (
    pathname &&
    userStatus?.currentWorkspace.isTrial &&
    !workspacePages.includes(pathname)
  ) {
    return TrialCounterHeight + IRM_HEADER_HEIGHT
  }

  return IRM_HEADER_HEIGHT
}

export default useHeaderHeight
